// components/common/header/AccountSection.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Avatar } from "antd";
import {
  FiUser,
  FiLogOut,
  FiShoppingBag,
  FiChevronDown,
  FiMapPin,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { PROFILE_ROUTE } from "../../../routes/constant";
import { logoutUser } from "../../../store/slices/authSlice";
import { openCityModal } from "../../../store/slices/citySlice";
import LoginModal from "../../auth/LoginModal";

const AccountSection = ({ isDarkMode, onLoginClick }) => {
  const dispatch = useDispatch();
  const { user, isLoggedIn } = useSelector((state) => state.auth);
  const { selectedCity } = useSelector((state) => state.city);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  const handleLogout = async () => {
    await dispatch(logoutUser());
  };

  const handleLoginClick = () => {
    setLoginModalVisible(true);
    if (onLoginClick) onLoginClick();
  };

  const handleCityClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(openCityModal());
  };

  // Authenticated navigation must not depend on a separate user-profile request.
  // During OTP login the token/session may be ready before the profile arrives.
  if (isLoggedIn) {
    return (
      <>
        <div className="flex items-center gap-2">
          {/* City Selector Button */}
          <button
            onClick={handleCityClick}
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
              ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}
            `}
          >
            <FiMapPin
              size={16}
              className={isDarkMode ? "text-green-400" : "text-green-600"}
            />
            <span
              className={`text-sm font-medium ${
                isDarkMode ? "text-white" : "text-gray-700"
              }`}
            >
              {selectedCity?.name || "Select City"}
            </span>
            <FiChevronDown
              size={12}
              className={isDarkMode ? "text-gray-400" : "text-gray-400"}
            />
          </button>

          {/* User Dropdown */}
          <div className="relative hidden group md:block">
            <button
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
                ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}
              `}
            >
              <Avatar
                src={user?.profile_pic}
                className="bg-green-600"
                size="small"
                style={{ width: 28, height: 28 }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
              <span
                className={`inline text-sm font-medium ${
                  isDarkMode ? "text-white" : "text-gray-700"
                }`}
              >
                {user?.name?.split(" ")[0] || "User"}
              </span>
              <FiChevronDown
                size={14}
                className={`block ${
                  isDarkMode ? "text-gray-400" : "text-gray-400"
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            <div
              className={`
                absolute right-0 mt-2 w-44 py-2 rounded-lg shadow-xl opacity-0 invisible 
                group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 text-sm
                ${
                  isDarkMode
                    ? "bg-gray-800 border border-gray-700"
                    : "bg-white border border-gray-200"
                }
              `}
            >
              <Link
                to={PROFILE_ROUTE}
                className={`
                  flex items-center gap-3 px-4 py-2.5 transition-colors
                  ${
                    isDarkMode
                      ? "text-white hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }
                `}
              >
                <FiUser
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
                <span>Profile</span>
              </Link>

              <Link
                to={`${PROFILE_ROUTE}?tab=orders`}
                className={`
                  flex items-center gap-3 px-4 py-2.5 transition-colors
                  ${
                    isDarkMode
                      ? "text-white hover:bg-gray-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }
                `}
              >
                <FiShoppingBag
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
                <span>Orders</span>
              </Link>

              <div
                className={`border-t my-2 ${
                  isDarkMode ? "border-gray-700" : "border-gray-200"
                }`}
              />

              <button
                onClick={handleLogout}
                className={`
                  flex items-center gap-3 px-4 py-2.5 w-full text-left transition-colors
                  ${
                    isDarkMode
                      ? "text-red-400 hover:bg-gray-700"
                      : "text-red-600 hover:bg-gray-100"
                  }
                `}
              >
                <FiLogOut
                  size={14}
                  className={isDarkMode ? "text-red-400" : "text-red-600"}
                />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        <LoginModal
          visible={loginModalVisible}
          onClose={() => setLoginModalVisible(false)}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          onClick={handleCityClick}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
            ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}
          `}
        >
          <FiMapPin
            size={16}
            className={isDarkMode ? "text-green-400" : "text-green-600"}
          />
          <span
            className={`text-sm font-medium ${
              isDarkMode ? "text-white" : "text-gray-700"
            }`}
          >
            {selectedCity?.name || "Select City"}
          </span>
          <FiChevronDown
            size={12}
            className={isDarkMode ? "text-gray-400" : "text-gray-400"}
          />
        </button>

        <button
          onClick={handleLoginClick}
          className={`
            hidden md:flex items-center gap-2 px-3 py-2 rounded-lg transition-colors
            ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}
          `}
        >
          <FiUser
            size={18}
            className={isDarkMode ? "text-gray-400" : "text-gray-600"}
          />
          <span
            className={`inline text-sm font-medium ${
              isDarkMode ? "text-white" : "text-gray-700"
            }`}
          >
            My Account
          </span>
        </button>
      </div>

      <LoginModal
        visible={loginModalVisible}
        onClose={() => setLoginModalVisible(false)}
      />
    </>
  );
};

export default AccountSection;
